'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { 
    Hospital, 
    Stethoscope, 
    Pill, 
    Package, 
    Menu,
    X,
    ShoppingCart,
    Wrench,
    ClipboardList,
    Settings,
    Users,
    FlaskConical,
    type LucideIcon
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * MobileBottomNav Molecule
 * 
 * Fixed bottom navigation bar for mobile devices.
 * Shows main navigation items with icons and labels.
 */

interface NavItem {
    href: string;
    label: string;
    icon: LucideIcon;
}

// Main visible tabs (max 5 for bottom nav)
const MAIN_ITEMS: NavItem[] = [
    { href: '/reception', label: 'Tiếp nhận', icon: Hospital },
    { href: '/examination', label: 'Khám', icon: Stethoscope },
    { href: '/prescriptions', label: 'Đơn thuốc', icon: Pill },
    { href: '/inventory', label: 'Kho', icon: Package },
];

// More items in expandable menu
const MORE_ITEMS: NavItem[] = [
    { href: '/lab', label: 'Xét nghiệm', icon: FlaskConical },
    { href: '/pos', label: 'Bán hàng', icon: ShoppingCart },
    { href: '/services', label: 'Dịch vụ', icon: Wrench },
    { href: '/orders', label: 'Đặt hàng', icon: ClipboardList },
    { href: '/settings', label: 'Cài đặt', icon: Settings },
    { href: '/users', label: 'Người dùng', icon: Users },
];

export function MobileBottomNav() {
    const pathname = usePathname();
    const [showMore, setShowMore] = useState(false);
    
    // Check if current page is in more items
    const isInMoreItems = MORE_ITEMS.some(item => pathname.startsWith(item.href));
    
    // Hide on auth pages and portal pages
    if (pathname === '/login' || pathname === '/register' || pathname.startsWith('/auth') || pathname.startsWith('/portal')) {
        return null;
    }

    return (
        <>
            {/* More Menu Overlay */}
            {showMore && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setShowMore(false)}
                />
            )}
            
            {/* More Menu Panel */}
            {showMore && (
                <div className="fixed bottom-16 left-0 right-0 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-700 z-50 md:hidden rounded-t-xl shadow-lg">
                    <div className="p-2 grid grid-cols-3 gap-1">
                        {MORE_ITEMS.map((item) => {
                            const Icon = item.icon;
                            const isActive = pathname.startsWith(item.href);
                            return (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setShowMore(false)}
                                    className={cn(
                                        "flex flex-col items-center justify-center py-3 px-2 rounded-lg transition-colors",
                                        isActive
                                            ? "bg-primary/10 text-primary"
                                            : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800"
                                    )}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="text-xs mt-1 truncate">{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Bottom Navigation Bar */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-700 z-50 md:hidden safe-area-bottom">
                <div className="flex items-center justify-around h-16">
                    {MAIN_ITEMS.map((item) => {
                        const Icon = item.icon;
                        const isActive = pathname.startsWith(item.href);
                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn(
                                    "flex flex-col items-center justify-center flex-1 h-full transition-colors",
                                    isActive
                                        ? "text-primary"
                                        : "text-gray-500 dark:text-gray-400"
                                )}
                            >
                                <Icon className={cn("w-5 h-5", isActive && "stroke-[2.5]")} />
                                <span className={cn(
                                    "text-[10px] mt-0.5",
                                    isActive && "font-medium"
                                )}>{item.label}</span>
                            </Link>
                        );
                    })}
                    
                    {/* More Button */}
                    <Button variant="ghost"  
                        onClick={() => setShowMore(!showMore)}
                        className={cn(
                            "flex flex-col items-center justify-center flex-1 h-full transition-colors",
                            (showMore || isInMoreItems)
                                ? "text-primary"
                                : "text-gray-500 dark:text-gray-400"
                        )}
                    >
                        {showMore ? (
                            <X className="w-5 h-5" />
                        ) : (
                            <Menu className="w-5 h-5" />
                        )}
                        <span className={cn(
                            "text-[10px] mt-0.5",
                            (showMore || isInMoreItems) && "font-medium"
                        )}>Thêm</span>
                    </Button>
                </div>
            </nav>
        </>
    );
}

export default MobileBottomNav;
